import { Box, Divider, IconButton, ListItemIcon, ListItemText, Menu, MenuItem } from "@mui/material"
import { CheckOutlined, MoreVertOutlined, PrintOutlined } from "@mui-symbols-material/w300"
import type { FC } from "react"
import { useRef, useState } from "react"

import { PALLET_LABEL_PRINTABLE_STATUSES } from "../../constants"

import type { AdjustmentHistoryItem } from "@/graphql/codegen/graphql"
import { InventoryAdjustmentStatus } from "@/graphql/codegen/graphql"

interface AdjustmentHistoryRowActionsProps {
  item: AdjustmentHistoryItem
  canMaintain: boolean
  isPrinting: boolean
  onWorkOrderConfirm: (item: AdjustmentHistoryItem) => Promise<void>
  onWorkOrderPrint: (item: AdjustmentHistoryItem) => void
  onPalletLabelPrintAll: (item: AdjustmentHistoryItem) => void
}

export const AdjustmentHistoryRowActions: FC<AdjustmentHistoryRowActionsProps> = ({
  item,
  canMaintain,
  isPrinting,
  onWorkOrderConfirm,
  onWorkOrderPrint,
  onPalletLabelPrintAll,
}) => {
  const anchorElRef = useRef<HTMLButtonElement>(null)
  const [isMenuOpen, setIsMenuOpen] = useState(false)

  const isWorkOrderPending = item.status === InventoryAdjustmentStatus.WorkOrderPending
  const canPrintQrCodes = PALLET_LABEL_PRINTABLE_STATUSES.includes(item.status)

  const handleMenuClose = () => setIsMenuOpen(false)

  const handleWorkOrderPrint = () => {
    handleMenuClose()
    onWorkOrderPrint(item)
  }

  const handleWorkOrderConfirm = () => {
    handleMenuClose()
    onWorkOrderConfirm(item)
  }

  const handlePalletLabelPrintAll = () => {
    handleMenuClose()
    onPalletLabelPrintAll(item)
  }

  return (
    <Box ref={anchorElRef}>
      <IconButton
        size='small'
        onClick={() => setIsMenuOpen(true)}
        data-testid='adjustment-history.row-actions.menu-button'
      >
        <MoreVertOutlined />
      </IconButton>
      <Menu
        anchorEl={anchorElRef.current}
        open={isMenuOpen}
        onClose={handleMenuClose}
        data-testid='adjustment-history.row-actions.menu'
      >
        <MenuItem onClick={handleWorkOrderPrint} data-testid='adjustment-history.row-actions.print-work-order-button'>
          <ListItemIcon>
            <PrintOutlined />
          </ListItemIcon>
          <ListItemText primary='Print Work Order' />
        </MenuItem>
        {isWorkOrderPending && canMaintain && (
          <MenuItem
            onClick={handleWorkOrderConfirm}
            data-testid='adjustment-history.row-actions.confirm-work-order-button'
          >
            <ListItemIcon>
              <CheckOutlined />
            </ListItemIcon>
            <ListItemText primary='Confirm Work Order' />
          </MenuItem>
        )}
        {canPrintQrCodes && (
          <>
            <Divider />
            <MenuItem
              onClick={handlePalletLabelPrintAll}
              disabled={isPrinting}
              data-testid='adjustment-history.row-actions.print-pallet-labels-button'
            >
              <ListItemIcon>
                <PrintOutlined />
              </ListItemIcon>
              <ListItemText primary='Print All QR Codes' />
            </MenuItem>
          </>
        )}
      </Menu>
    </Box>
  )
}
